/**
 * Asaas Payments Management API
 *
 * APIs para resellers criarem e gerenciarem cobranças via Asaas BaaS.
 */

import api from './api'

// ============================================================================
// Types
// ============================================================================

export type CreateChargeRequest = {
  contract_id: string
  vm_instance_id?: string
  amount: number
  description: string
  due_date: string // ISO format: YYYY-MM-DD
  payment_method: 'BOLETO' | 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD'
}

export type CreatePIXChargeRequest = {
  contract_id: string
  vm_instance_id?: string
  amount: number
  description: string
  expiration_minutes?: number // Default: 60
}

export type ChargeResponse = {
  transaction_id: string
  asaas_payment_id: string
  invoice_url: string | null
  gross_amount: number
  status: string
  payment_method: string
  due_date: string | null
  qr_code?: string // PIX copia e cola
  qr_code_url?: string // URL da imagem do QR Code
  created_at: string
}

export type CancelPaymentResponse = {
  message: string
  transaction_id: string
  status: string
}

// Manual charges types
export type ManualChargeRequest = {
  contract_id: string
  amount: number
  description: string
  due_date?: string
  notes?: string
}

export type ManualChargeUpdateRequest = {
  description?: string
  amount?: number
  due_date?: string
  notes?: string
}

export type RegisterPaymentRequest = {
  amount: number
  payment_date: string
  payment_method: string // bank_transfer | pix_external | boleto_external | cash | other
  description?: string
  reference?: string
}

export type ManualPaymentRecord = {
  id: string
  transaction_id: string
  amount: number
  payment_date: string
  payment_method: string
  payment_method_label: string
  description?: string
  reference?: string
  created_by: string
  created_at: string
}

export type ManualChargeResponse = {
  id: string
  contract_id: string
  customer_id: string
  source: string
  gross_amount: number
  paid_amount: number
  status: string
  description: string
  notes?: string
  due_date?: string
  payment_method: string
  created_at: string
  manual_payments_count: number
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Cria uma cobrança com split automático B2B2B
 */
export const createCharge = async (data: CreateChargeRequest): Promise<ChargeResponse> => {
  const response = await api.post('/api/v1/asaas/charges', data)
  return response.data
}

/**
 * Cria uma cobrança PIX com QR Code
 */
export const createPIXCharge = async (data: CreatePIXChargeRequest): Promise<ChargeResponse> => {
  const response = await api.post('/api/v1/asaas/charges/pix', data)
  return response.data
}

/**
 * Consulta status de uma cobrança
 */
export const getChargeStatus = async (transactionId: string): Promise<ChargeResponse> => {
  const response = await api.get(`/api/v1/asaas/charges/${transactionId}`)
  return response.data
}

/**
 * Cancela uma cobrança pendente
 */
export const cancelCharge = async (transactionId: string): Promise<CancelPaymentResponse> => {
  const response = await api.delete(`/api/v1/asaas/charges/${transactionId}`)
  return response.data
}

// ============================================================================
// Manual Charges API Functions
// ============================================================================

/**
 * Cria uma cobrança manual (ERP externo)
 */
export const createManualCharge = async (
  data: ManualChargeRequest,
): Promise<ManualChargeResponse> => {
  const response = await api.post('/api/v1/manual-charges', data)
  return response.data
}

/**
 * Registra pagamento em cobrança manual
 */
export const registerPayment = async (
  transactionId: string,
  data: RegisterPaymentRequest,
): Promise<ManualPaymentRecord> => {
  const response = await api.post(`/api/v1/manual-charges/${transactionId}/payments`, data)
  return response.data
}

/**
 * Lista pagamentos de uma cobrança manual
 */
export const listPayments = async (transactionId: string): Promise<ManualPaymentRecord[]> => {
  const response = await api.get(`/api/v1/manual-charges/${transactionId}/payments`)
  return response.data
}

/**
 * Atualiza cobrança manual
 */
export const updateManualCharge = async (
  transactionId: string,
  data: ManualChargeUpdateRequest,
): Promise<ManualChargeResponse> => {
  const response = await api.put(`/api/v1/manual-charges/${transactionId}`, data)
  return response.data
}

/**
 * Cancela cobrança manual
 */
export const cancelManualCharge = async (transactionId: string): Promise<CancelPaymentResponse> => {
  const response = await api.delete(`/api/v1/manual-charges/${transactionId}`)
  return response.data
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formata valor monetário para exibição
 */
export const formatCurrency = (value: number, currency: string = 'BRL'): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Formata data ISO para exibição
 */
export const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

/**
 * Formata data e hora ISO para exibição
 */
export const formatDateTime = (isoDate: string): string => {
  const date = new Date(isoDate)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/**
 * Retorna label do status de pagamento
 */
export const getPaymentStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    partial: 'Parcial',
    confirmed: 'Confirmado',
    received: 'Recebido',
    overdue: 'Vencido',
    cancelled: 'Cancelado',
    refunded: 'Estornado',
  }
  return labels[status] || status
}

/**
 * Retorna cor do badge do status
 */
export const getPaymentStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    partial: 'bg-amber-100 text-amber-800',
    confirmed: 'bg-blue-100 text-blue-800',
    received: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
    refunded: 'bg-purple-100 text-purple-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Retorna label do método de pagamento
 */
export const getPaymentMethodLabel = (method: string): string => {
  const labels: Record<string, string> = {
    boleto: 'Boleto Bancário',
    pix: 'PIX',
    credit_card: 'Cartão de Crédito',
    debit_card: 'Cartão de Débito',
    manual: 'Manual',
    BOLETO: 'Boleto Bancário',
    PIX: 'PIX',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
  }
  return labels[method] || method
}

/**
 * Retorna label do método de pagamento manual
 */
export const getManualPaymentMethodLabel = (method: string): string => {
  const labels: Record<string, string> = {
    bank_transfer: 'Transferência Bancária',
    pix_external: 'PIX (externo)',
    boleto_external: 'Boleto (externo)',
    cash: 'Dinheiro',
    other: 'Outro',
  }
  return labels[method] || method
}

/**
 * Lista de métodos de pagamento manual disponíveis
 */
export const MANUAL_PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Transferência Bancária' },
  { value: 'pix_external', label: 'PIX (externo)' },
  { value: 'boleto_external', label: 'Boleto (externo)' },
  { value: 'cash', label: 'Dinheiro' },
  { value: 'other', label: 'Outro' },
]

// ============================================================================
// Export default object
// ============================================================================

const asaasPaymentsApi = {
  createCharge,
  createPIXCharge,
  getChargeStatus,
  cancelCharge,
  // Manual charges
  createManualCharge,
  registerPayment,
  listPayments,
  updateManualCharge,
  cancelManualCharge,
  // Helper functions
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentStatusLabel,
  getPaymentStatusColor,
  getPaymentMethodLabel,
  getManualPaymentMethodLabel,
  MANUAL_PAYMENT_METHODS,
}

export default asaasPaymentsApi
